import {getCurrentCulture} from "@services/litium-service";

export type DateParts = {
  year: number;
  month: number;
  day: number;
};

export const DateFormat = "yyyy/MM/dd";

export const useDate = () => {
  const parse = (date: string | null | undefined): DateParts | null => {
    if (!date) {
      return null;
    }

    const isoDateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
    if (isoDateMatch) {
      const [, yearText, monthText, dayText] = isoDateMatch;
      const year = Number(yearText);
      const month = Number(monthText);
      const day = Number(dayText);

      if (!isValidDateParts(year, month, day)) {
        return null;
      }

      return { year, month, day };
    }

    const [year, month, day] = date.split(/[/-]/).map(Number);

    if (!isValidDateParts(year, month, day)) {
      return null;
    }

    return { year, month, day };
  };

  const toDateString = (date: Date | string | null | undefined): string | null => {
    if (!date) {
      return null;
    }

    if (date instanceof Date) {
      return formatDateParts({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      });
    }

    const parts = parse(date);
    return parts ? formatDateParts(parts) : null;
  };

  const createLocalDate = (date: string | null | undefined): Date | null => {
    const parts = parse(date);

    if (!parts) {
      return null;
    }

    return new Date(parts.year, parts.month - 1, parts.day);
  };

  const formatForDisplay = (
    date: string,
    options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    }
  ): string => {
    const parts = parse(date);

    if (!parts) {
      return "";
    }

    const locale = getCurrentCulture();

    return new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: "UTC",
    }).format(new Date(Date.UTC(parts.year, parts.month - 1, parts.day)));
  };

  return {
    DateFormat,
    parse,
    toDateString,
    createLocalDate,
    formatForDisplay,
  };
};

function formatDateParts(parts: DateParts): string {
  const month = `${parts.month}`.padStart(2, "0");
  const day = `${parts.day}`.padStart(2, "0");

  return `${parts.year}/${month}/${day}`;
}

function isValidDateParts(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return false;
  }

  const utcDate = new Date(Date.UTC(year, month - 1, day));

  return utcDate.getUTCFullYear() === year
    && utcDate.getUTCMonth() === month - 1
    && utcDate.getUTCDate() === day;
}
